import axios from 'axios';
import React, { FormEvent, useEffect, useState } from 'react';
import { StringTranslations } from '@services';

type ChatMsgProps = {
  contentId: string
  dbCollectionName: string
  isForLiveBlogging: boolean
  isFromModerator: boolean
  lang: string
  stringTranslations: StringTranslations | null | undefined
}

function SendChatMessage({
  contentId,
  dbCollectionName,
  isForLiveBlogging = false,
  isFromModerator = false,
  lang,
  stringTranslations,
}: ChatMsgProps) {
  const [userName, setUserName] = useState<string>('');
  const [chatMsg, setChatMsg] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [userNameSubmitted, setUserNameSubmitted] = useState<boolean>(false);
  const [sendingMessage, setSendingMessage] = useState<boolean>(false);

  useEffect(() => {
    const storedUserName = localStorage.getItem('liveChatUserName');
    if (storedUserName) {
      setUserName(storedUserName);
      setUserNameSubmitted(true);
    }
  }, []);

  // reserve chd.tv team (case insensitive) username for admin/moderators
  const isInvalidUsername = (name: string) => !isFromModerator && name.toLowerCase().includes('chd.tv team');

  async function sendMessage(e: FormEvent) {
    e.preventDefault();
    setSendingMessage(true);
    if (errorMessage) setErrorMessage('');
    if (!userName) {
      setErrorMessage('Enter valid username.');
      return;
    }
    const sendError = 'Sorry, we ran into a problem sending your message. Please try sending it again.';
    // send new msg POST to gatsby function for processing
    try {
      if (isInvalidUsername(userName)) {
        setErrorMessage(`Invalid Username: ${userName}`);
        localStorage.removeItem('liveChatUserName');
        setSendingMessage(false);
        setUserName('');
        return;
      }
      localStorage.setItem('liveChatUserName', userName);
      if (!userNameSubmitted) setUserNameSubmitted(true);
      const { data: msgRespData } = await axios.post('/api/chat/send-message', {
        chatMsg,
        contentId,
        dbCollectionName,
        isFromModerator,
        userName,
      });
      if (!msgRespData.success) setErrorMessage(sendError);
      setChatMsg('');
    } catch (err: any) {
      setErrorMessage(sendError);
      setSendingMessage(false);
    }
    setSendingMessage(false);
  }

  return (
    <div id="sendMessageForm" className={`mt-4 px-2 w-full md:px-0 md:mb-1 lg:mt-2 ${!isForLiveBlogging ? 'bg-black' : 'bg-white'}`}>
      <form onSubmit={sendMessage}>
        <div className="flex flex-col">
          <div className="my-1">
            {!userNameSubmitted ? (
              <input
                className="w-full text-white transition-none input input-sm bg-charcoal hover:transition-none"
                name="userName"
                onChange={(e) => { setUserName(e.target.value); }}
                placeholder="Enter name"
                required
                type="text"
                value={userName}
              />
            ) : (
              <div className={`flex justify-between text-xs py-1 ${!isForLiveBlogging ? 'text-white' : 'text-black'}`}>
                <p>
                  {stringTranslations?.chatting_as[lang] || 'Chatting as'}
                  {' '}
                  {userName}
                </p>
                <button
                  className="underline"
                  type="button"
                  onClick={() => { setUserName(''); setUserNameSubmitted(false); localStorage.removeItem('liveChatUserName'); }}
                >
                  {stringTranslations?.leave_chat[lang] || 'Leave Chat'}
                </button>
              </div>
            )}
          </div>
          <div className="flex items-start gap-2">
            <div className="flex-auto w-4/5 my-1">
              {sendingMessage && (
              <p className="text-xs">
                <em>
                  {stringTranslations?.sending_message[lang] || 'Sending message'}
                  ...
                </em>
              </p>
              )}
              {!sendingMessage && (
                <textarea
                  rows={isForLiveBlogging ? 5 : 2}
                  className="w-full p-1 text-white transition-none input input-sm bg-charcoal hover:transition-none max-h-[200px]"
                  name="userName"
                  onChange={(e) => setChatMsg(e.target.value)}
                  placeholder="Write a comment..."
                  required
                  type="text"
                  value={chatMsg}
                />
              )}
            </div>
            <button
              className={`${isForLiveBlogging && 'bg-chdBlue p-2 rounded'} flex-auto w-1/5 my-1 text-white btn btn-sm`}
              disabled={sendingMessage}
              type="submit"
            >
              {stringTranslations?.send[lang] || 'Send'}
            </button>
          </div>
        </div>
      </form>
      {errorMessage && <p className="text-[15px] text-amber-500 text-center">{`!! ${errorMessage} !!`}</p>}
    </div>
  );
}

export default SendChatMessage;
